import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { api } from '../lib/api'
import { klassenbuchApi, type ScheduleGrid } from '../lib/klassenbuchApi'
import { X, Search, BookOpen } from 'lucide-react'

interface Course {
  id: number
  name: string
  fach: string
  jahrgang: string
  beschreibung: string
  farbe: string
  icon: string
  max_teilnehmer: number
}

interface Group {
  id: number
  name: string
  gruppen_typ: string
  beschreibung: string
  farbe: string
  icon: string
  max_teilnehmer: number
}

interface EditModalProps {
  type: 'course' | 'group' | 'class'
  item: Course | Group | Class
  onClose: () => void
  onSubmit: (data: any) => void
  isLoading: boolean
}

interface Class {
  id: number
  class_name: string
  grade_level: string
  department: string
  room: string
  class_teacher_id: number | null
  max_students: number
  description: string
  is_active: boolean
  schedule_grid_id?: string | null
}

type EditFormData = {
  name: string
  beschreibung: string
  fach: string[]
  jahrgang: string
  gruppen_typ: string
  farbe: string
  icon: string
  max_teilnehmer: number
  curricula: number[]
  schedule_grid_id?: string
}

export default function EditModal({ type, item, onClose, onSubmit, isLoading }: EditModalProps) {
  // Parse existing fach string to array
  const parseFachToArray = (fachString: string): string[] => {
    if (!fachString) return []
    return fachString.split(',').map(s => s.trim()).filter(s => s.length > 0)
  }

  const initialFormData: EditFormData = (() => {
    if (type === 'course') {
      const course = item as Course
      return {
        name: course.name || '',
        beschreibung: course.beschreibung || '',
        fach: parseFachToArray(course.fach || ''),
        jahrgang: course.jahrgang || '',
        gruppen_typ: 'arbeitsgruppe',
        farbe: course.farbe || '#3B82F6',
        icon: course.icon || 'book',
        max_teilnehmer: course.max_teilnehmer || 30,
        curricula: [] as number[]
      }
    }

    if (type === 'group') {
      const group = item as Group
      return {
        name: group.name || '',
        beschreibung: group.beschreibung || '',
        fach: [] as string[],
        jahrgang: '',
        gruppen_typ: group.gruppen_typ || 'arbeitsgruppe',
        farbe: group.farbe || '#3B82F6',
        icon: group.icon || 'book',
        max_teilnehmer: group.max_teilnehmer || 10,
        curricula: [] as number[]
      }
    }

    const klass = item as Class
    return {
      name: klass.class_name || '',
      beschreibung: klass.description || '',
      fach: [] as string[],
      jahrgang: klass.grade_level || '',
      gruppen_typ: 'arbeitsgruppe',
      farbe: '#3B82F6',
      icon: 'book',
      max_teilnehmer: klass.max_students || 10,
      curricula: [] as number[],
      schedule_grid_id: klass.schedule_grid_id || ''
    }
  })()

  const [formData, setFormData] = useState<EditFormData>(initialFormData)

  // Fetch subjects and curricula for course editing
  const { data: subjectsResponse } = useQuery(
    ['subjects'],
    () => api.get('/assignments/subjects/public').then(res => res.data),
    {
      enabled: type === 'course'
    }
  )

  const { data: curriculaResponse } = useQuery(
    ['curricula'],
    () => api.get('/assignments/curricula/public').then(res => res.data),
    {
      enabled: type === 'course'
    }
  )

  const { data: scheduleConfigDoc } = useQuery(
    ['klassenbuch-schedule-config-doc'],
    () => klassenbuchApi.getConfig(),
    {
      enabled: type === 'class'
    }
  )

  const subjects = subjectsResponse?.data || []
  const curricula = curriculaResponse?.data || []
  const scheduleGrids: ScheduleGrid[] = scheduleConfigDoc?.grids || []

  // Modal states
  const [showSubjectModal, setShowSubjectModal] = useState(false)
  const [showCurriculumModal, setShowCurriculumModal] = useState(false)
  const [subjectSearchTerm, setSubjectSearchTerm] = useState('')
  const [curriculumSearchTerm, setCurriculumSearchTerm] = useState('')
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>(formData.fach)
  const [selectedCurricula, setSelectedCurricula] = useState<number[]>(formData.curricula)

  // Load course details with curricula when editing a course
  const { data: courseDetails } = useQuery(
    ['course', item.id],
    () => api.get(`/courses/${item.id}`).then(res => res.data),
    {
      enabled: type === 'course' && !!item.id
    }
  )

  // Update formData when course details are loaded
  React.useEffect(() => {
    if (courseDetails?.data) {
      const course = courseDetails.data
      const curricula = course.curricula || []
      setFormData(prev => ({
        ...prev,
        curricula: curricula.map((c: any) => c.id)
      }))
      setSelectedCurricula(curricula.map((c: any) => c.id))
    }
  }, [courseDetails])

  // Filter functions
  const filteredSubjects = subjects.filter((subject: any) => {
    if (!subject) return false
    const search = subjectSearchTerm.toLowerCase()
    const name = (subject.subject_name || '').toLowerCase()
    const department = (subject.department || '').toLowerCase()
    const description = (subject.description || '').toLowerCase()
    
    return name.includes(search) || 
           department.includes(search) || 
           description.includes(search)
  })

  const filteredCurricula = curricula.filter((curriculum: any) => {
    if (!curriculum) return false
    const search = curriculumSearchTerm.toLowerCase()
    const name = (curriculum.name || '').toLowerCase()
    const subject = (curriculum.subject || '').toLowerCase()
    const examType = (curriculum.exam_type || '').toLowerCase()
    const description = (curriculum.description || '').toLowerCase()
    
    return name.includes(search) || 
           subject.includes(search) || 
           examType.includes(search) || 
           description.includes(search)
  })

  // Subject selection functions
  const toggleSubject = (subjectName: string) => {
    const newSelection = selectedSubjects.includes(subjectName)
      ? selectedSubjects.filter(name => name !== subjectName)
      : [...selectedSubjects, subjectName]
    setSelectedSubjects(newSelection)
  }

  const handleSelectAllSubjects = () => {
    if (selectedSubjects.length === filteredSubjects.length) {
      setSelectedSubjects([])
    } else {
      setSelectedSubjects(filteredSubjects.map((s: any) => s.subject_name))
    }
  }

  const handleSubjectSelection = () => {
    setFormData({ ...formData, fach: selectedSubjects })
    setShowSubjectModal(false)
  }

  // Curriculum selection functions
  const toggleCurriculum = (curriculumId: number) => {
    const newSelection = selectedCurricula.includes(curriculumId)
      ? selectedCurricula.filter(id => id !== curriculumId)
      : [...selectedCurricula, curriculumId]
    setSelectedCurricula(newSelection)
  }

  const handleSelectAllCurricula = () => {
    if (selectedCurricula.length === filteredCurricula.length) {
      setSelectedCurricula([])
    } else {
      setSelectedCurricula(filteredCurricula.map((c: any) => c.id))
    }
  }

  const handleCurriculumSelection = () => {
    setFormData({ ...formData, curricula: selectedCurricula })
    setShowCurriculumModal(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (type === 'class') {
      onSubmit({
        ...formData,
        schedule_grid_id: formData.schedule_grid_id ? String(formData.schedule_grid_id) : null
      })
      return
    }
    onSubmit(formData)
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {type === 'course' ? 'Kurs bearbeiten' : type === 'group' ? 'Gruppe bearbeiten' : 'Klasse bearbeiten'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            {type === 'course' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fächer</label>
                  <button
                    type="button"
                    onClick={() => setShowSubjectModal(true)}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-left focus:ring-primary-500 focus:border-primary-500 bg-white hover:bg-gray-50"
                  >
                    {formData.fach.length === 0 
                      ? 'Fächer auswählen...' 
                      : `${formData.fach.length} Fach${formData.fach.length !== 1 ? 'er' : ''} ausgewählt`
                    }
                  </button>
                  {formData.fach.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {formData.fach.map(subjectName => (
                        <span
                          key={subjectName}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800"
                        >
                          {subjectName}
                          <button
                            type="button"
                            onClick={() => {
                              const newSubjects = formData.fach.filter(name => name !== subjectName)
                              setFormData({ ...formData, fach: newSubjects })
                            }}
                            className="ml-1 text-green-600 hover:text-green-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Curricula</label>
                  <button
                    type="button"
                    onClick={() => setShowCurriculumModal(true)}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-left focus:ring-primary-500 focus:border-primary-500 bg-white hover:bg-gray-50"
                  >
                    {formData.curricula.length === 0 
                      ? 'Curricula auswählen...' 
                      : `${formData.curricula.length} Curriculum${formData.curricula.length !== 1 ? 'a' : ''} ausgewählt`
                    }
                  </button>
                  {formData.curricula.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {formData.curricula.map(curriculumId => {
                        const curriculum = curricula.find((c: any) => c.id === curriculumId)
                        return curriculum ? (
                          <span
                            key={curriculumId}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                          >
                            {curriculum.name}
                            <button
                              type="button"
                              onClick={() => {
                                const newCurricula = formData.curricula.filter(id => id !== curriculumId)
                                setFormData({ ...formData, curricula: newCurricula })
                              }}
                              className="ml-1 text-blue-600 hover:text-blue-800"
                            >
                              ×
                            </button>
                          </span>
                        ) : null
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Jahrgang</label>
                  <input
                    type="text"
                    value={formData.jahrgang}
                    onChange={(e) => setFormData({ ...formData, jahrgang: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </>
            )}
            
            {type === 'group' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Gruppentyp</label>
                <select
                  value={formData.gruppen_typ}
                  onChange={(e) => setFormData({ ...formData, gruppen_typ: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="arbeitsgruppe">Arbeitsgruppe</option>
                  <option value="projektgruppe">Projektgruppe</option>
                  <option value="lernpartner">Lernpartner</option>
                  <option value="sitzgruppe">Sitzgruppe</option>
                  <option value="sonstige">Sonstige</option>
                </select>
              </div>
            )}

            {type === 'class' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Zeitraster</label>
                <select
                  value={formData.schedule_grid_id || ''}
                  onChange={(e) => setFormData({ ...formData, schedule_grid_id: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Standard-Zeitraster</option>
                  {scheduleGrids.map((grid) => (
                    <option key={grid.id} value={grid.id}>
                      {grid.name}
                      {scheduleConfigDoc?.defaultGridId === grid.id ? ' (Standard)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Max. Teilnehmer</label>
              <input
                type="number"
                min="1"
                max="100"
                value={formData.max_teilnehmer}
                onChange={(e) => setFormData({ ...formData, max_teilnehmer: parseInt(e.target.value) })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Farbe</label>
              <input
                type="color"
                value={formData.farbe}
                onChange={(e) => setFormData({ ...formData, farbe: e.target.value })}
                className="mt-1 block w-full h-10 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {isLoading ? 'Speichere...' : 'Speichern'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Subject Selection Modal */}
      {showSubjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Fächer auswählen
                {subjects.length > 0 && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({subjects.length} verfügbar)
                  </span>
                )}
              </h3>
              <button
                onClick={() => setShowSubjectModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
                title="Schließen"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={subjectSearchTerm}
                  onChange={(e) => setSubjectSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Nach Name, Abteilung oder Beschreibung suchen..."
                />
              </div>
            </div>

            {/* Select All Button */}
            {filteredSubjects.length > 0 && (
              <div className="mb-4 flex justify-between items-center p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={handleSelectAllSubjects}
                    className="text-sm text-green-700 hover:text-green-800 font-medium"
                  >
                    {selectedSubjects.length === filteredSubjects.length && filteredSubjects.length > 0 
                      ? 'Alle abwählen' 
                      : 'Alle auswählen'
                    }
                  </button>
                  <span className="text-sm text-green-700 ml-2">
                    ({selectedSubjects.length} von {filteredSubjects.length} ausgewählt)
                  </span>
                </div>
              </div>
            )}
            
            <div className="flex-1 overflow-y-auto">
              {subjects.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Keine Fächer verfügbar</p>
                </div>
              ) : filteredSubjects.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Keine Fächer gefunden</p>
                  <p className="text-sm">Versuchen Sie einen anderen Suchbegriff</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredSubjects.map((subject: any) => (
                    <div
                      key={subject.id}
                      className={`p-4 border rounded-lg bg-white shadow-sm flex items-center justify-between cursor-pointer ${
                        selectedSubjects.includes(subject.subject_name) ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200'
                      }`}
                      onClick={() => toggleSubject(subject.subject_name)}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedSubjects.includes(subject.subject_name)}
                          onChange={() => toggleSubject(subject.subject_name)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 text-sm">
                            {subject.subject_name}
                          </h4>
                          {subject.department && (
                            <p className="text-xs text-gray-600">
                              {subject.department}
                            </p>
                          )}
                          {subject.description && (
                            <p className="text-xs text-gray-500">
                              {subject.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowSubjectModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleSubjectSelection}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                {selectedSubjects.length} Fach{selectedSubjects.length !== 1 ? 'er' : ''} auswählen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Curriculum Selection Modal */}
      {showCurriculumModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Curricula auswählen
                {curricula.length > 0 && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({curricula.length} verfügbar)
                  </span>
                )}
              </h3>
              <button
                onClick={() => setShowCurriculumModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
                title="Schließen"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={curriculumSearchTerm}
                  onChange={(e) => setCurriculumSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Nach Name, Fach, Prüfungstyp oder Beschreibung suchen..."
                />
              </div>
            </div>

            {/* Select All Button */}
            {filteredCurricula.length > 0 && (
              <div className="mb-4 flex justify-between items-center p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={handleSelectAllCurricula}
                    className="text-sm text-blue-700 hover:text-blue-800 font-medium"
                  >
                    {selectedCurricula.length === filteredCurricula.length && filteredCurricula.length > 0 
                      ? 'Alle abwählen' 
                      : 'Alle auswählen'
                    }
                  </button>
                  <span className="text-sm text-blue-700 ml-2">
                    ({selectedCurricula.length} von {filteredCurricula.length} ausgewählt)
                  </span>
                </div>
              </div>
            )}
            
            <div className="flex-1 overflow-y-auto">
              {curricula.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Keine Curricula verfügbar</p>
                </div>
              ) : filteredCurricula.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Keine Curricula gefunden</p>
                  <p className="text-sm">Versuchen Sie einen anderen Suchbegriff</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredCurricula.map((curriculum: any) => (
                    <div
                      key={curriculum.id}
                      className={`p-4 border rounded-lg bg-white shadow-sm flex items-center justify-between cursor-pointer ${
                        selectedCurricula.includes(curriculum.id) ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                      }`}
                      onClick={() => toggleCurriculum(curriculum.id)}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedCurricula.includes(curriculum.id)}
                          onChange={() => toggleCurriculum(curriculum.id)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 text-sm">
                            {curriculum.name}
                          </h4>
                          <p className="text-xs text-gray-600">
                            {curriculum.subject}
                            {curriculum.exam_type && ` • ${curriculum.exam_type}`}
                          </p>
                          {curriculum.scale_title && (
                            <p className="text-xs text-gray-500">
                              {curriculum.scale_title}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowCurriculumModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleCurriculumSelection}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                {selectedCurricula.length} Curriculum{selectedCurricula.length !== 1 ? 'a' : ''} auswählen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
